import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { CountryCodeService } from 'src/app/_services/countrycode.service';
import { PartnerService } from 'src/app/_services/partner.service';
import { RentalPartnerService } from 'src/app/_services/rental/rental-partner.service';
import { YachtPartnerService } from 'src/app/_services/yacht/yacht-partner.service';
import { ToastrService } from 'ngx-toastr';
import {
  countryMobilePhoneValidator,
  getMobileDigitRangeForDialCode,
  getPhoneLengthErrorMessageForDialCode,
} from 'src/app/_helpers/phone-number.validator';

/**
 * Shared modal to update a partner's SPOC phone number.
 * Used by car, yacht and rental partner detail pages.
 *
 * fromParent: {
 *   module: 'partner' | 'yachtPartner' | 'rentalPartner',
 *   partnerId: string,
 *   countryCode: string,
 *   mobileNumber: string,
 * }
 */
@Component({
  selector: 'app-spoc-phone-update-modal',
  templateUrl: './spoc-phone-update-modal.component.html',
  styleUrls: ['./spoc-phone-update-modal.component.scss']
})
export class SpocPhoneUpdateModalComponent implements OnInit {
  @Input() fromParent: any;
  spocPhoneForm: FormGroup;
  loading: boolean = false;
  countryList: any[] = [];
  countryCode: string;
  partnerId: string = '';
  initialMobileNumber: string = '';
  initialCountryCode: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private countryCodeService: CountryCodeService,
    public modal: NgbModal,
    private toastrService: ToastrService,
    private partnerService: PartnerService,
    private yachtPartnerService: YachtPartnerService,
    private rentalPartnerService: RentalPartnerService,
  ) {}

  ngOnInit(): void {
    this.getCountryList();
    this.initializeForm();
    this.prefillForm();
  }

  initializeForm() {
    this.spocPhoneForm = this.formBuilder.group({
      mobileNumber: ['', [
        Validators.required,
        countryMobilePhoneValidator(() => this.currentDialCode)
      ]],
      countryCode: ['', [Validators.required]],
    });
  }

  get currentDialCode(): string {
    return this.spocPhoneForm?.get('countryCode')?.value || this.countryCode || this.initialCountryCode || '';
  }

  get phoneMaxLength(): number {
    return getMobileDigitRangeForDialCode(this.currentDialCode).max;
  }

  get phoneMinLength(): number {
    return getMobileDigitRangeForDialCode(this.currentDialCode).min;
  }

  get phoneErrorMessage(): string {
    return getPhoneLengthErrorMessageForDialCode(this.currentDialCode);
  }

  private prefillForm() {
    if (!this.fromParent) {
      return;
    }

    this.partnerId = this.fromParent.partnerId || '';

    const mobileNumber = this.fromParent.mobileNumber || '';
    const countryCode = this.fromParent.countryCode || '';

    this.spocPhoneForm.patchValue({
      mobileNumber: mobileNumber,
      countryCode: countryCode
    });

    // Ensure ng-select displays the current country code
    this.countryCode = countryCode;

    // Store initial values for change detection
    this.initialMobileNumber = mobileNumber;
    this.initialCountryCode = countryCode;

    // Disable country code field (not editable - must be preserved)
    this.spocPhoneForm.get('countryCode')?.disable();
    this.spocPhoneForm.get('mobileNumber')?.updateValueAndValidity();
  }

  getCountryList() {
    this.countryCodeService.getCountry().subscribe((res) => {
      this.countryList = res;
    });
  }

  private getUpdateRequest(body: { countryCode: string; mobileNumber: string }): Observable<any> | null {
    switch (this.fromParent?.module) {
      case 'partner':
        return this.partnerService.updateSpocPhone(this.partnerId, body);
      case 'yachtPartner':
        return this.yachtPartnerService.updateSpocPhone(this.partnerId, body);
      case 'rentalPartner':
        return this.rentalPartnerService.updateSpocPhone(this.partnerId, body);
      default:
        return null;
    }
  }

  private refreshPartner() {
    switch (this.fromParent?.module) {
      case 'partner':
        this.partnerService.setRefreshPartner(true);
        break;
      case 'yachtPartner':
        this.yachtPartnerService.setRefreshPartner(true);
        break;
      case 'rentalPartner':
        this.rentalPartnerService.setRefreshPartner(true);
        break;
    }
  }

  spocPhoneSubmit(value: any) {
    if (!this.partnerId) {
      this.toastrService.error('Partner ID is missing', 'Error');
      return;
    }

    if (this.spocPhoneForm.invalid) {
      this.spocPhoneForm.markAllAsTouched();
      return;
    }

    // Get country code from form control directly since disabled fields aren't in form.value
    const countryCode = this.spocPhoneForm.get('countryCode')?.value || this.initialCountryCode;

    // Check if mobile number has changed
    const mobileNumberChanged = value.mobileNumber.trim() !== this.initialMobileNumber.trim();

    if (!mobileNumberChanged) {
      this.toastrService.info('No changes detected', 'Info');
      return;
    }

    const body = {
      countryCode: countryCode,
      mobileNumber: value.mobileNumber.trim()
    };

    const request = this.getUpdateRequest(body);
    if (!request) {
      this.toastrService.error('Unknown partner type', 'Error');
      return;
    }

    this.loading = true;

    request.subscribe(
      (response: any) => {
        this.loading = false;
        if (response === true || response?.statusCode === 200 || response?.code === 200 || response?.data === true) {
          this.toastrService.success('SPOC phone number updated successfully', 'Success');
          this.refreshPartner();
          this.modal.dismissAll();
        } else {
          this.toastrService.error(response?.message || 'Failed to update SPOC phone number', 'Error');
        }
      },
      (error: any) => {
        this.loading = false;
        this.toastrService.error(this.extractApiError(error), 'Error');
      }
    );
  }

  onlyAllowNumbers(event: any, maxLength: number) {
    const char = String.fromCharCode(event.which);
    if (!/[0-9]/.test(char)) {
      event.preventDefault();
      return;
    }
    const current = this.spocPhoneForm.get('mobileNumber')?.value || '';
    if (current.length >= maxLength) {
      event.preventDefault();
    }
  }

  onPaste(event: any) {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text/plain');
    const numericText = pastedText.replace(/[^0-9]/g, '').slice(0, this.phoneMaxLength);
    event.target.value = numericText;

    const control = this.spocPhoneForm.get('mobileNumber');
    if (control) {
      control.setValue(numericText);
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  private extractApiError(error: any): string {
    const body = error?.error;
    const message = body?.message;
    const custom = body?.CustomBadRequestError;

    if (typeof custom === 'string' && /phone number/i.test(custom)) {
      return this.phoneErrorMessage;
    }
    if (message && message !== 'BAD_REQUEST') {
      return message;
    }
    if (message === 'BAD_REQUEST' || error?.status === 400) {
      return this.phoneErrorMessage;
    }
    return error?.message || 'An error occurred while updating SPOC phone number';
  }

  customSearchFn(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase()) ||
           item.dial_code.toLowerCase().includes(term.toLowerCase());
  }

  hasFormChanged(): boolean {
    const currentMobileNumber = this.spocPhoneForm.get('mobileNumber')?.value || '';
    return currentMobileNumber.trim() !== this.initialMobileNumber.trim();
  }
}
